import React, {useState} from 'react';
import Alert from '@mui/material/Alert';
import Fab from '@mui/material/Fab';
import ReactDOM from 'react-dom';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PolygonIcon from '../../icons/PolygonIcon';
import CancelIcon from '@mui/icons-material/Cancel';
import FormControl from '@mui/material/FormControl';
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import {emitCustomEvent} from 'react-custom-events';
import Events from '../../defaults/Events';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import PolylineIcon from '@mui/icons-material/Polyline';
import {useIFPRSGlobalContext} from "../../IFPRSGlobalContext";
import './CreatePerimeterToolbar.css';
import {useSelector} from 'react-redux';
import {Box, Typography} from "@mui/material";
import {useEffect} from 'react';
import FieldNames from "../../defaults/FieldNames.json";
import {
    createFeatureCompleted, checkIfPointBuffer,
    generateFeatureCollectionFromShapefile, getEventAndJoinFields
} from "../../utils/UploadHelper";
import Graphic from "@arcgis/core/Graphic";
import {getOperationType} from '../../utils/UploadHelper';

function CreatePerimeterToolbar({
                                    onCreatePerimeterCancel,
                                    onDrawComplete,
                                    isTreatment,
                                    isCreatingActualFeature,
                                    isCreatingPlannedFeature,
                                    feature,
                                    onReplacePerimeter,
                                    isCreatingPostFire,
                                    isCreatingProject
                                }) {
    const [showShapeParseError, setShowShapeParseError] = useState(false);
    const [showUploadGeometryError, setShowUploadGeometryError] = useState(false);
    const inputReaderRef = React.useRef(null);
    const config = useSelector((state) => state.configuration.config);
    const [isUploadPostFire, setIsUploadPostFire] = useState(false);

    const uploadShapefile = () => {
        setIsUploadPostFire(false);
        inputReaderRef.current.click();
    }

    const uploadPostFireShapefile = () => {
        setIsUploadPostFire(true);
        inputReaderRef.current.click();
    }

    const drawNewFeature = () => {
        emitCustomEvent(Events.EnableDrawMode,
            {
                geometry: null,
                callbackParams: {
                    feature: feature,
                    joinFeature: FieldNames.joinFeatureValues.polygon,
                    isTreatment: isTreatment,
                    isPostFire: false,
                    isProject: false,
                    customEvent: Events.EstimatedPolygonCreated,
                    onDrawComplete: onDrawComplete,
                },
                callback: createFeatureCompleted
            }
        );
    }

    const drawNewPostFireFeature = (isProject) => {
        const callbackParams = {
            feature: feature,
            joinFeature: FieldNames.joinFeatureValues.polygon,
            isTreatment: isTreatment,
            isPostFire: true,
            isProject: false,
            customEvent: Events.EstimatedPolygonCreated,
            onDrawComplete: onDrawComplete
        };
        if (isProject) {
            callbackParams.isProject = true;
            callbackParams.isTreatment = false;
        }
        emitCustomEvent(Events.EnableDrawMode,
            {
                geometry: null,
                callbackParams: callbackParams,
                callback: createFeatureCompleted
            }
        );
    }

    const selectNewFeaturePoint = () => {
        emitCustomEvent(Events.ToggleIsMovingPoint, false);
        emitCustomEvent(Events.EnablePointMode,
            {
                geometry: null,
                callbackParams: {
                    feature: feature,
                    joinFeature: FieldNames.joinFeatureValues.polygon,
                    isTreatment: isTreatment,
                    isPostFire: false,
                    isProject: false,
                    isPoint: true,
                    customEvent: Events.EstimatedPolygonCreated,
                    onDrawComplete: onDrawComplete,
                },
                callback: createFeatureCompleted
            });
    }

    const selectNewPostFireFeatureLine = () => {
        emitCustomEvent(Events.EnablePostFireLineMode,
            {
                geometry: null,
                callbackParams: {
                    feature: feature,
                    joinFeature: FieldNames.joinFeatureValues.line,
                    isTreatment: isTreatment,
                    isPostFire: true,
                    isProject: false,
                    customEvent: Events.EstimatedLineCreated,
                    onDrawComplete: onDrawComplete,
                },
                callback: createFeatureCompleted
            }
        );
    }

    const selectNewPostFireFeaturePoint = () => {
        emitCustomEvent(Events.ToggleIsMovingPoint, false);
        emitCustomEvent(Events.EnablePostFirePointMode,
            {
                geometry: null,
                callbackParams: {
                    feature: feature,
                    joinFeature: FieldNames.joinFeatureValues.point,
                    isTreatment: isTreatment,
                    isPostFire: true,
                    isProject: false,
                    customEvent: Events.EstimatedPointCreated,
                    onDrawComplete: onDrawComplete,
                },
                callback: createFeatureCompleted
            });
    }

    const {setAlertMessageObj} = useIFPRSGlobalContext();

    const shapefileChanged = async (e) => {
        const file = e.target.files && e.target.files.length > 0 ? e.target.files[0] : null;
        if (file) {
            try {
                setShowUploadGeometryError(false);
                await generateFeatureCollection(file);
                setShowShapeParseError(false);
            } catch (e){
                (e.cause === 'GeometryTypeMismatch') ? setShowUploadGeometryError(true) : setShowShapeParseError(true);
                console.error("Error generating feature collection from shapefile:", e);
            }
        }
        if (inputReaderRef.current) {
            inputReaderRef.current.value = "";
        }
    };

    useEffect(() => {
        if (showShapeParseError === true) {
            setAlertMessageObj({
                message: "Shapefile Parse Error",
                severity: "error",
                child: (
                    <Typography sx={{marginLeft: "5px", marginRight: "5px"}}>
                        Unable to parse shapefile or found empty geometry. Please verify .zip contents and geometry and
                        try again.
                    </Typography>
                ),
                callback: null
            });
        }
        // Cleanup function to reset showShapeParseError to false
        return () => {
            setShowShapeParseError(false);
        };
    }, [showShapeParseError, setAlertMessageObj]);

    useEffect(() => {
        if (showUploadGeometryError) {
            const operationType = getOperationType(isCreatingPostFire, isTreatment, isCreatingProject);
            setAlertMessageObj({
                message: 'Upload Geometry Error',
                severity: "error",
                child: (
                    <Typography sx={{ marginLeft: "5px", marginRight: "5px" }}>
                        Uploaded geometry type is not valid for {operationType?.name}. Please verify .zip contents and try again.
                    </Typography>
                ),
                callback: null
            });
        }
        // Cleanup function to reset showUploadGeometryError to false
        return () => {
          setShowUploadGeometryError(false);
        };
    }, [showUploadGeometryError, setAlertMessageObj]);

    const generateFeatureCollection = async (file) => {
        const tempPerimeter = await generateFeatureCollectionFromShapefile(file, config);
        if (tempPerimeter) {
            // Create point buffer polygon for New Fuel Activities
            const perimeter = checkIfPointBuffer(tempPerimeter, isUploadPostFire, isTreatment, isCreatingProject);
            try {
                const {event, joinField} = getEventAndJoinFields(perimeter.type, isUploadPostFire, isTreatment, isCreatingProject);
                const params = {
                    feature: perimeter,
                    isTreatment: isTreatment,
                    joinFeature: joinField,
                    isPostFire: isUploadPostFire,
                    customEvent: event,
                    isProject: isCreatingProject,
                    onDrawComplete: onDrawComplete
                }
                // createFeatureCompleted expects a Geometry object, but that doesn't have a constructor, so we go through new Graphic first.
                const newGraphic = new Graphic({
                    geometry: perimeter
                });
                createFeatureCompleted(newGraphic.geometry, params);
            } catch (e) {
                throw e
            }
        }
    }

    return ReactDOM.createPortal(
        (
            <div className="edit-geometry-container edit-geometry-selector" id="EditGeometryToolbarAnchorEl">

                {(!isCreatingPostFire) &&
                    <Fab variant="extended" size="medium" color="primary" aria-label="Upload" onClick={uploadShapefile}>
                        <UploadFileIcon sx={{mr: 1}}/>
                        Upload
                    </Fab>
                }
                {(isCreatingPostFire) &&
                    <Fab variant="extended" size="medium" color="primary" aria-label="Upload"
                         onClick={uploadPostFireShapefile}>
                        <UploadFileIcon sx={{mr: 1}}/>
                        Upload
                    </Fab>
                }

                {(isCreatingPlannedFeature && !isCreatingPostFire) &&
                    <Fab variant="extended" size="medium" color="primary" aria-label="Draw"
                         onClick={() => drawNewFeature()}>
                        <PolygonIcon sx={{mr: 1}}/>
                        Polygon
                    </Fab>
                }

                {((isCreatingPlannedFeature || isCreatingProject) && isCreatingPostFire) &&
                    <Fab variant="extended" size="medium" color="primary" aria-label="Draw"
                         onClick={() => drawNewPostFireFeature(isCreatingProject)}>
                        <PolygonIcon sx={{mr: 1}}/>
                        Polygon
                    </Fab>
                }

                {/* Fuels Feature Point */}
                {(isCreatingPlannedFeature && !isCreatingPostFire && !isTreatment) &&
                    <Fab variant="extended" size="medium" color="primary" aria-label="Point"
                         onClick={() => selectNewFeaturePoint()}>
                        <GpsFixedIcon sx={{mr: 1}}/>
                        Point
                    </Fab>
                }

                {/* Post-Fire Feature Point */}
                {(isCreatingPlannedFeature && isCreatingPostFire && !isCreatingProject) &&
                    <Fab variant="extended" size="medium" color="primary" aria-label="Point"
                         onClick={() => selectNewPostFireFeaturePoint()}>
                        <GpsFixedIcon sx={{mr: 1}}/>
                        Point
                    </Fab>
                }

                {/* Post-Fire Feature Line */}
                {(isCreatingPlannedFeature && isCreatingPostFire && isTreatment && !isCreatingProject) &&
                    <Fab variant="extended" size="medium" color="primary" aria-label="Point"
                         onClick={() => selectNewPostFireFeatureLine()}>
                        <PolylineIcon sx={{mr: 1}}/>
                        Line
                    </Fab>
                }

                <Fab variant="extended" size="medium" color="primary" aria-label="Draw"
                     onClick={() => onCreatePerimeterCancel('planned')}>
                    <CancelIcon sx={{mr: 1}}/>
                    Cancel
                </Fab>
                <FormControl>
                    <input type="file" ref={inputReaderRef} accept=".zip" multiple={false} style={{display: 'none'}}
                           onChange={shapefileChanged}/>
                </FormControl>
            </div>), document.getElementById("editPerimeterAnchorEl")
    );

}

export default CreatePerimeterToolbar;

